import type { CopilotCardSlotId } from 'owa-copilot-ccf-store';
import { createStore } from 'owa-satcheljs';
import { mutatorAction } from 'satcheljs';

interface CopilotCardSlotActivityStore {
    /** Slots a card has been shown into at some point in this session. */
    usedSlots: Map<CopilotCardSlotId, true>;
}

const getStore: () => CopilotCardSlotActivityStore = createStore<CopilotCardSlotActivityStore>(
    'copilotCardSlotActivity',
    {
        usedSlots: new Map<CopilotCardSlotId, true>(),
    }
);

/**
 * Whether a mount point for `slot` needs to exist at all.
 *
 * This is the gate a mount point renders behind, and it is deliberately the *only* piece of card
 * state CCF asks a caller to keep eager. The card store, the cache and the selectors that answer
 * "is there a card for this exact key?" all sit behind the lazy view boundary, so a session that
 * never triggers a card never downloads any of it.
 *
 * Deliberately coarse: it answers per slot rather than per slot and context key, and it never
 * goes back to false once a card has been shown. Both are safe in the direction that matters. It
 * is only ever wrong by returning true when the precise answer is false, in which case the host
 * mounts, finds no card for its context and renders nothing — a chunk that was already
 * downloaded and a component that returns `null`. It cannot be wrong the other way, because it
 * is set as part of showing a card, before any host is asked to render one.
 */
export function mayHaveCopilotCard(slot: CopilotCardSlotId): boolean {
    return getStore().usedSlots.has(slot);
}

/** Records that `slot` now has a card, so its mount point starts rendering. */
export const markCopilotCardSlotUsedMutator: (slot: CopilotCardSlotId) => void = mutatorAction(
    'CCF_MARK_SLOT_USED',
    (slot: CopilotCardSlotId): void => {
        getStore().usedSlots.set(slot, true);
    }
);

/**
 * Clears the recorded slots. Test-only: the flag never clears in a real session, which is the
 * whole reason it is safe, so tests need a way back to a clean session.
 */
export function resetCopilotCardSlotActivityForTest(): void {
    getStore().usedSlots.clear();
}
